import { DairyInsurancePriceModel, InsuranceState } from '../../../../../Generated/Raven-Demeter';
import { TableDatum, TableHeader } from '../../../../Components/Tables/Table/TableDefinitions';
import languageService from '../../../../Services/Language/LanguageService';
import { translationKeys } from '../../../../Services/Language/TranslationDefinitions';
import { LiquidityRiskCalculationInputs } from '../../RiskDefinitions';

export const defaultClass3MilkPercent = 100;
export const defaultClass4MilkPercent = 0;
export const defaultButterFatPercentage = 4;
export const defaultProteinPercentage = 3.5;
export const defaultDeclaredComponentPriceWeighting = 0;
export const defaultDeclaredMilkProduction = 0;
export const defaultCoverageLevelPercent = 0;
export const defaultDeclaredSharePercent = 100;

export type DairyRevenueProtectionCalculationInputs = {
    insuranceYear: number;
    coverageEffectiveDate: string;
    insuredSharedPercentage: number;
    insuranceState: InsuranceState;
    componentBasedPercent?: number;
    classBasePercent: number;
    coverageLevelPercent: number;
    protectionFactor: number;
    declaredSharePercent: number;
    declaredMilkProduction: number;
    class3MilkPercent: number;
    class4MilkPercent: number;
    fatPercent?: number;
    proteinPercent?: number;
    componentWeightingFactorPercent: number;
};

export type GeneralRiskCalculationInputs = LiquidityRiskCalculationInputs | DairyRevenueProtectionCalculationInputs;

export type DairySelectionTableRow = DairyInsurancePriceModel & {
    title?: string;
    isSelected?: boolean;
    actions?: JSX.Element;
};

export type DairyPremiumInformationTableRow = {
    endorsementType: string;
    revenueGuarenteePerCwt: number;
    declaredMilkProduction: number;
    expectedYield: number;
    revenueGuarentee: number;
    premiumBeforeSubsidy: number;
    premiumBeforeSubsidyCwt: number;
    subsidy: number;
    subsidyPerCwt: number;
    premiumAfterSubsidy: number;
    premiumAfterSubsidyPerCwt: number;
};

export const protectionFactorOptions = [
    {
        label: '1.00',
        value: 1.0,
    },
    {
        label: '1.05',
        value: 1.05,
    },
    {
        label: '1.10',
        value: 1.1,
    },
    {
        label: '1.15',
        value: 1.15,
    },
    {
        label: '1.20',
        value: 1.2,
    },
    {
        label: '1.25',
        value: 1.25,
    },
    {
        label: '1.30',
        value: 1.3,
    },
    {
        label: '1.35',
        value: 1.35,
    },
    {
        label: '1.40',
        value: 1.4,
    },
    {
        label: '1.45',
        value: 1.45,
    },
    {
        label: '1.50',
        value: 1.5,
    },
];

const defaultHundredWeightDecimalPlaces = 2;
const defaultDecimalPlaces = 0;

export const getQuarter = (tableDatum: TableDatum<DairySelectionTableRow> | DairyInsurancePriceModel) => {
    const month = new Date(tableDatum.effectiveStartDate).getMonth() + 1;

    return `Q${Math.ceil(month / 3)} ${new Date(tableDatum.effectiveStartDate).getFullYear()}`;
};

export const dairySelectionTableHeaders: TableHeader<DairySelectionTableRow>[] = [
    {
        headerValueGetter: () => languageService.translate(translationKeys.words.quarter),
        field: 'quarter',
        cellRenderer: getQuarter,
    },
    {
        headerValueGetter: () => languageService.translate(translationKeys.risk.margin.headers.class3Milk),
        field: 'class3MilkPrice',
        dataFormat: 'currency',
    },
    {
        headerValueGetter: () => languageService.translate(translationKeys.risk.margin.headers.class4Milk),
        field: 'class4MilkPrice',
        dataFormat: 'currency',
    },
    {
        headerValueGetter: () => languageService.translate(translationKeys.risk.margin.headers.butterFat),
        field: 'butterfatPrice',
        dataFormat: 'currency',
    },
    {
        headerValueGetter: () => languageService.translate(translationKeys.risk.margin.headers.protein),
        field: 'proteinPrice',
        dataFormat: 'currency',
    },
    {
        headerValueGetter: () => languageService.translate(translationKeys.risk.margin.headers.otherSolids),
        field: 'otherSolidsPrice',
        dataFormat: 'currency',
    },
    {
        headerValueGetter: () => languageService.translate(translationKeys.risk.margin.headers.nonfatSolids),
        field: 'nonfatSolidsPrice',
        dataFormat: 'currency',
    },
    {
        headerValueGetter: () => languageService.translate(translationKeys.risk.margin.headers.yield),
        field: 'expectedYield',
        dataFormat: 'currency',
    },
];

export const dairyPremiumInformationTableHeaders: TableHeader<DairyPremiumInformationTableRow>[] = [
    {
        header: '',
        field: 'endorsementType',
        dataFormat: 'string',
        minimumWidth: 250,
    },
    {
        headerValueGetter: () => languageService.translate(translationKeys.risk.margin.headers.declaredMilkProduction),
        field: 'declaredMilkProduction',
        dataFormat: 'currency',
    },
    {
        headerValueGetter: () => languageService.translate(translationKeys.risk.margin.headers.yield),
        field: 'expectedYield',
        dataFormat: 'currency',
        displayDecimalPlaces: defaultDecimalPlaces,
    },
    {
        headerValueGetter: () => languageService.translate(translationKeys.risk.margin.headers.revenueGuarentee),
        field: 'revenueGuarentee',
        dataFormat: 'currency',
        displayDecimalPlaces: defaultDecimalPlaces,
    },
    {
        headerValueGetter: () => languageService.translate(translationKeys.risk.margin.headers.revenueGuarenteePerCwt),
        field: 'revenueGuarenteePerCwt',
        dataFormat: 'currency',
        displayDecimalPlaces: defaultHundredWeightDecimalPlaces,
    },
    {
        headerValueGetter: () => languageService.translate(translationKeys.risk.margin.headers.premiumBeforeSubsidy),
        field: 'premiumBeforeSubsidy',
        dataFormat: 'currency',
        displayDecimalPlaces: defaultDecimalPlaces,
    },
    {
        headerValueGetter: () => languageService.translate(translationKeys.risk.margin.headers.premiumBeforeSubsidyCwt),
        field: 'premiumBeforeSubsidyCwt',
        dataFormat: 'currency',
        displayDecimalPlaces: defaultHundredWeightDecimalPlaces,
    },
    {
        headerValueGetter: () => languageService.translate(translationKeys.risk.margin.headers.subsidy),
        field: 'subsidy',
        dataFormat: 'currency',
        displayDecimalPlaces: defaultDecimalPlaces,
    },
    {
        headerValueGetter: () => languageService.translate(translationKeys.risk.margin.headers.subsidyPerCwt),
        field: 'subsidyPerCwt',
        dataFormat: 'currency',
        displayDecimalPlaces: defaultHundredWeightDecimalPlaces,
    },
    {
        headerValueGetter: () => languageService.translate(translationKeys.risk.margin.headers.premiumAfterSubsidy),
        field: 'premiumAfterSubsidy',
        dataFormat: 'currency',
        displayDecimalPlaces: defaultDecimalPlaces,
    },
    {
        headerValueGetter: () => languageService.translate(translationKeys.risk.margin.headers.premiumAfterSubsidyPerCwt),
        field: 'premiumAfterSubsidyPerCwt',
        dataFormat: 'currency',
        displayDecimalPlaces: defaultHundredWeightDecimalPlaces,
    },
];
